import React, { useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { formatHandlerCodeUniversal } from "../../utils/handlerCodeUniversalFormatter";
import { colorHandlerCodeUniversal } from "../../utils/colorEnumFormatter";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { StoredFileDTO, SystemDataType } from "../../interfaces/DTO/storageFile";
import { useContactFiles, useUpdateContactFileMetadata } from "../../hooks/crm/useContacts";
import { formatName } from "../../utils/namesFormater";
import ContactFileUploader from "../CRM/contacts/ContactFileUploader";
import ContactFilePreviewDialog from "../CRM/contacts/ContactFilePreviewDialog";

interface FileCardProps {
  file: StoredFileDTO;
  contactId: number;
  onPreview: () => void;
  onTagSaved: () => void;
}

const FileCard: React.FC<FileCardProps> = ({ file, contactId, onPreview, onTagSaved }) => {
  const [hovered, setHovered] = useState(false);
  const [editingTag, setEditingTag] = useState(false);
  const [tagValue, setTagValue] = useState(file.custom_tag ?? "");
  const updateMetadata = useUpdateContactFileMetadata(contactId);

  const handleSaveTag = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await updateMetadata.mutateAsync({
      full_path: file.full_path,
      metadata: {
        system_data_type: file.system_data_type,
        target_handler_code: file.target_handler_code,
        custom_tag: tagValue,
      },
    });
    setEditingTag(false);
    onTagSaved();
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTagValue(file.custom_tag ?? "");
    setEditingTag(true);
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Enter") handleSaveTag(e as unknown as React.MouseEvent);
    if (e.key === "Escape") setEditingTag(false);
  };

  return (
    <Card
      variant="outlined"
      sx={{ width: 180, flexShrink: 0, position: "relative" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <CardActionArea onClick={onPreview} sx={{ p: 1.5 }}>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <InsertDriveFileIcon sx={{ fontSize: 40, color: "primary.main" }} />
            <Typography
              variant="caption"
              align="center"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
                lineHeight: 1.3,
              }}
            >
              {file.name}
            </Typography>

            {file.target_handler_code && (
              <Chip
                label={formatHandlerCodeUniversal(file.target_handler_code)}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: colorHandlerCodeUniversal(file.target_handler_code),
                  borderColor: colorHandlerCodeUniversal(file.target_handler_code),
                  color: "#fff",
                }}
              />
            )}

            {/* תגית מותאמת */}
            <Box
              sx={{ width: "100%", minHeight: 28, display: "flex", alignItems: "center", justifyContent: "center" }}
              onClick={(e) => e.stopPropagation()}
            >
              {editingTag ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
                  <TextField
                    size="small"
                    value={tagValue}
                    onChange={(e) => setTagValue(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    autoFocus
                    inputProps={{ style: { fontSize: 11, padding: "2px 4px" } }}
                    sx={{ flex: 1 }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Tooltip title="שמור">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={handleSaveTag}
                      disabled={updateMetadata.isPending}
                      sx={{ p: 0.25 }}
                    >
                      <CheckIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {file.custom_tag ? (
                    <Chip
                      label={file.custom_tag}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ fontSize: 10 }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                      {hovered ? "הוסף תגית" : ""}
                    </Typography>
                  )}
                  {hovered && (
                    <Tooltip title="ערוך תגית">
                      <IconButton size="small" onClick={handleEditClick} sx={{ p: 0.25 }}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

interface ContactInsuranceFilesCardsProps {
  contact: Contact;
}

const ContactInsuranceFilesCards: React.FC<ContactInsuranceFilesCardsProps> = ({ contact }) => {
  const contactId = contact.id!;
  const { data: allFiles = [], isLoading, refetch } = useContactFiles(contactId);
  const files = allFiles.filter(
    (f) => f.system_data_type === SystemDataType.INSURANCE_COVERAGE_REPORT
  );
  const [previewFile, setPreviewFile] = useState<StoredFileDTO | null>(null);

  const handleDownload = async (fullPath: string, fileName: string) => {
    const baseUrl = process.env.REACT_APP_API_URL || "";
    const encoded = encodeURIComponent(fullPath);
    const url = `${baseUrl}/contacts/${contactId}/stream-file?full_path=${encoded}`;
    const response = await fetch(url);
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleOpenInNewTab = (fullPath: string) => {
    const baseUrl = process.env.REACT_APP_API_URL || "";
    const encoded = encodeURIComponent(fullPath);
    window.open(`${baseUrl}/contacts/${contactId}/stream-file?full_path=${encoded}`, "_blank");
  };

  const previewUrl = previewFile
    ? `${process.env.REACT_APP_API_URL || ""}/contacts/${contactId}/stream-file?full_path=${encodeURIComponent(previewFile.full_path)}`
    : null;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {formatName(contact)}
        </Typography>
        <ContactFileUploader
          contactId={contactId}
          showUploadArea={false}
          initialSystemDataType={SystemDataType.INSURANCE_COVERAGE_REPORT}
          onClose={() => {}}
          onUploadSuccess={refetch}
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : files.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          לא נמצאו עדיין קבצי פוליסות ביטוח
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          {files.map((file, idx) => (
            <FileCard
              key={idx}
              file={file}
              contactId={contactId}
              onPreview={() => setPreviewFile(file)}
              onTagSaved={refetch}
            />
          ))}
        </Box>
      )}

      <ContactFilePreviewDialog
        open={!!previewFile}
        previewUrl={previewUrl}
        fileName={previewFile?.name ?? ""}
        fullPath={previewFile?.full_path ?? ""}
        onClose={() => setPreviewFile(null)}
        onDownload={handleDownload}
        onOpenInNewTab={handleOpenInNewTab}
      />
    </Box>
  );
};

export default ContactInsuranceFilesCards;
